import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import TrialBanner from '../common/TrialBanner';
import useUIStore from '../../store/uiStore';
import { useAuth } from '../../context/AuthContext';
import { Sparkles } from 'lucide-react';

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  
  // Refrescar datos del usuario al montar el layout (captura cambios de plan del admin)
  useEffect(() => {
    refreshUser();
  }, [refreshUser]);
  
  // Zustand store
  const { 
    isSidebarOpen, 
    closeSidebar,
    isUpgradeModalOpen, 
    upgradeModalFeature,
    openUpgradeModal, 
    closeUpgradeModal 
  } = useUIStore();

  // Escucha evento global para abrir modal de upgrade (conexión Axios <-> Zustand)
  useEffect(() => {
    const handleShowUpgrade = (event) => {
      const feature = event.detail?.feature || 'general';
      openUpgradeModal(feature);
    };

    window.addEventListener('show-upgrade-modal', handleShowUpgrade);
    return () => window.removeEventListener('show-upgrade-modal', handleShowUpgrade);
  }, [openUpgradeModal]);

  // Contenido dinámico del modal basado en el feature
  const modalContent = useMemo(() => {
    switch (upgradeModalFeature) {
      case 'Inventario Avanzado':
        return {
          title: 'Desbloquea el Control de Stock',
          description: 'La gestión de Inventario y Alertas de Stock es exclusiva de los planes Imperio Profesional y superiores.',
          buttonText: 'Mejorar a Plan Imperio',
          icon: '📦',
          gradient: 'from-[#2a9d8f] to-[#227f74]'
        };
      case 'Rentabilidad Financiera':
        return {
          title: 'Domina tus Finanzas 📈',
          description: 'El análisis de Rentabilidad y Gráficos Financieros es exclusivo de los planes Imperio Profesional y superiores. ¡Lleva tu clínica al siguiente nivel!',
          buttonText: 'Mejorar a Plan Imperio',
          icon: '💰',
          gradient: 'from-emerald-500 to-teal-600'
        };
      case 'citas_limit':
      case 'limit_reached':
        return {
          title: '¡Prueba Completada!',
          description: 'Espero que te haya gustado crear tu primer registro. Para seguir gestionando tu clínica sin límites, activa tu plan ahora.',
          buttonText: 'Ver Planes Disponibles',
          icon: '🎉',
          gradient: 'from-blue-600 to-indigo-600'
        };
      default:
        return {
          title: 'Función Premium',
          description: 'Esta funcionalidad requiere un plan superior. Revisa nuestros planes para acceder a todas las características.',
          buttonText: 'Ver Planes',
          icon: '⭐',
          gradient: 'from-purple-500 to-indigo-600'
        };
    }
  }, [upgradeModalFeature]);

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Mobile sidebar backdrop */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={closeSidebar}
        />
      )}
      
      <Sidebar />
      
      <div className="flex-1 flex flex-col overflow-hidden lg:ml-0">
        <Header />
        
        {/* Trial Banner integrated at the top of the main content area */}
        <TrialBanner />

        <main 
          id="main-content"
          className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 p-4 lg:p-6"
          role="main"
          aria-label="Contenido principal"
        >
          {children}
        </main>
      </div>

      {/* Global Upgrade Modal - Contenido Dinámico */}
      {isUpgradeModalOpen && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all scale-100">
            <div className={`bg-gradient-to-r ${modalContent.gradient} p-6 text-center`}>
              <span className="text-4xl mb-2 block">{modalContent.icon}</span>
              <h3 className="text-2xl font-bold text-white">
                {modalContent.title}
              </h3>
            </div>
            
            <div className="p-8 text-center">
              <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                {modalContent.description}
              </p>
              
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    closeUpgradeModal();
                    navigate('/app/suscripcion');
                  }}
                  className={`w-full px-6 py-3.5 bg-gradient-to-r ${modalContent.gradient} text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all flex items-center justify-center gap-2`}
                >
                  <Sparkles className="w-5 h-5" />
                  {modalContent.buttonText}
                </button>
                <button
                  onClick={closeUpgradeModal}
                  className="w-full px-6 py-2 text-gray-400 hover:text-gray-600 font-medium transition-colors"
                >
                  Quizás más tarde
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;